import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "card",
    "stack",
    "counter",
    "addOverlay",
    "skipOverlay",
    "done",
    "doneCount",
    "actions",
    "filterPanel",
    "cardView",
    "recipeView",
    "viewCard",
    "viewRecipe",
    "dayPicker",
    "progressDot",
  ]
  static values = { weekStart: String, openSlots: Number, createUrl: String }

  connect() {
    this.currentIndex = 0
    this.initialCount = parseInt(this.counterTarget.textContent, 10) || 0
    this.newAdds = 0
    this.startX = 0
    this.currentX = 0
    this.currentView = "card"
  }

  setView(event) {
    const view = event.currentTarget.dataset.view
    this.currentView = view

    const active = "text-white bg-warm-600"
    const inactive = "bg-white dark:bg-gray-800 text-gray-400"

    this.viewCardTarget.className = `p-1.5 ${view === "card" ? active : inactive}`
    this.viewRecipeTarget.className = `p-1.5 ${view === "recipe" ? active : inactive}`

    this.cardViewTargets.forEach((el) =>
      el.classList.toggle("hidden", view !== "card")
    )
    this.recipeViewTargets.forEach((el) =>
      el.classList.toggle("hidden", view !== "recipe")
    )
  }

  flipToRecipe() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    card.querySelector("[data-quick-plan-target='cardView']")?.classList.add("hidden")
    card.querySelector("[data-quick-plan-target='recipeView']")?.classList.remove("hidden")
  }

  flipToCard() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    card.querySelector("[data-quick-plan-target='recipeView']")?.classList.add("hidden")
    card.querySelector("[data-quick-plan-target='cardView']")?.classList.remove("hidden")
  }

  flip(e) {
    if (e?.target?.closest("button, a, form, select")) return
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    const photo = card.querySelector("[data-photo-view]")
    const recipe = card.querySelector("[data-recipe-view]")
    const actions = card.querySelector("[data-photo-actions]")
    if (!photo || !recipe) return
    photo.classList.toggle("hidden")
    recipe.classList.toggle("hidden")
    if (actions) actions.classList.toggle("hidden", photo.classList.contains("hidden"))
  }

  toggleFilters() {
    this.filterPanelTarget.classList.toggle("hidden")
  }

  add() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return

    this.pendingRecipeId = card.dataset.recipeId

    if (this.hasDayPickerTarget) {
      this.dayPickerTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    } else {
      this.saveEntry(this.pendingRecipeId)
      this.newAdds++
      this.updateCounter()
      this.animateCard(card, "right")
    }
  }

  selectDay(event) {
    const button = event.currentTarget
    const date = button.dataset.date
    if (!this.pendingRecipeId || !date) return

    const card = this.cardTargets[this.currentIndex]

    // Server replies with a Turbo Stream that swaps the picker row in
    // place — see meal_plan_entries#create.turbo_stream.erb. No manual
    // outerHTML hack; the row is rendered from the same partial both at
    // page load and after save, so it stays consistent.
    this.saveEntry(this.pendingRecipeId, date)

    // Close modal after brief delay so the user sees the row flip to "saved"
    setTimeout(() => {
      this.closeDayPicker()
      this.newAdds++
      this.updateCounter()
      if (card) this.animateCard(card, "right")
    }, 500)
  }

  closeDayPicker() {
    if (this.hasDayPickerTarget) {
      this.dayPickerTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  favorite(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const url = btn.dataset.favoriteUrl
    const method = btn.dataset.favoriteMethod
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const svg = btn.querySelector("svg")
    const isFavorited = svg.classList.contains("fill-current")

    if (isFavorited) {
      svg.classList.remove("fill-current", "text-warm-500")
      svg.classList.add("text-white")
      svg.setAttribute("fill", "none")
      btn.dataset.favoriteMethod = "post"
    } else {
      svg.classList.add("fill-current", "text-warm-500")
      svg.classList.remove("text-white")
      svg.setAttribute("fill", "currentColor")
      btn.dataset.favoriteMethod = "delete"
    }

    fetch(url, {
      method: method.toUpperCase(),
      headers: { "X-CSRF-Token": token, "Accept": "text/html" },
    })
  }

  skip() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return

    this.animateCard(card, "left")
  }

  // --- Pointer handlers (unified touch + mouse) ---

  pointerdown(event) {
    if (event.target?.closest("button, a, form, select, input")) return
    this.dragging = true
    this.dragDirection = null
    this.startX = event.clientX
    this.startY = event.clientY
    this.currentX = 0
    this.currentY = 0
    this.element.setPointerCapture?.(event.pointerId)
  }

  pointermove(event) {
    if (!this.dragging) return
    const dx = event.clientX - this.startX
    const dy = this.startY - event.clientY // positive = up

    // Lock direction after 10px
    if (!this.dragDirection && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      this.dragDirection = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical"
    }

    if (!this.dragDirection) return

    const card = this.cardTargets[this.currentIndex]
    if (!card) return

    if (this.dragDirection === "horizontal") {
      this.currentX = dx
      card.style.transform = `translateX(${dx}px) rotate(${dx * 0.03}deg)`
      card.style.transition = "none"
    } else if (this.dragDirection === "vertical" && dy > 0) {
      this.currentY = dy
      const clamped = Math.min(dy, 120)
      card.style.transform = `translateY(${-clamped * 0.5}px)`
      card.style.transition = "none"
      const addOverlay = card.querySelector("[data-quick-plan-target='addOverlay']")
      if (addOverlay) addOverlay.style.opacity = Math.min(clamped / 80, 1)
    }
  }

  pointerup() {
    if (!this.dragging) return
    this.dragging = false

    const card = this.cardTargets[this.currentIndex]
    if (!card) return

    if (this.dragDirection === "horizontal" && Math.abs(this.currentX) > 80) {
      // Horizontal swipe — browse next/prev
      const dir = this.currentX > 0 ? 400 : -400
      card.style.transition = "transform 0.3s ease-out, opacity 0.3s"
      card.style.transform = `translateX(${dir}px) rotate(${dir * 0.03}deg)`
      card.style.opacity = "0"
      setTimeout(() => {
        card.style.transform = ""
        card.style.opacity = ""
        card.style.transition = ""
        this.currentX > 0 ? this._showPrev() : this._showNext()
      }, 300)
      return
    } else if (this.dragDirection === "vertical" && this.currentY > 80) {
      // Swipe up — add to plan
      card.style.transform = ""
      card.style.transition = ""
      const addOverlay = card.querySelector("[data-quick-plan-target='addOverlay']")
      if (addOverlay) addOverlay.style.opacity = 0
      this.add()
      return
    }

    // Bounce back
    card.style.transition = "transform 0.2s ease-out"
    card.style.transform = ""
    const addOverlay = card.querySelector("[data-quick-plan-target='addOverlay']")
    if (addOverlay) addOverlay.style.opacity = 0
    setTimeout(() => { card.style.transition = "" }, 200)
  }

  pointercancel() {
    if (!this.dragging) return
    this.dragging = false
    this._resetCard()
  }

  _showNext() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    card.classList.add("hidden")
    this.currentIndex++
    if (this.currentIndex >= this.cardTargets.length) {
      this.currentIndex = 0
      this.cardTargets.forEach((c) => c.classList.add("hidden"))
    }
    this.cardTargets[this.currentIndex]?.classList.remove("hidden")
  }

  _showPrev() {
    if (this.currentIndex <= 0) return
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    card.classList.add("hidden")
    this.currentIndex--
    this.cardTargets[this.currentIndex]?.classList.remove("hidden")
  }

  _resetCard() {
    const card = this.cardTargets[this.currentIndex]
    if (!card) return
    card.style.transform = ""
    card.style.transition = ""
    const addOverlay = card.querySelector("[data-quick-plan-target='addOverlay']")
    if (addOverlay) addOverlay.style.opacity = 0
  }

  // --- Keyboard ---

  keydown(e) {
    if (e.key === "ArrowLeft") { e.preventDefault(); this._showPrev() }
    else if (e.key === "ArrowRight") { e.preventDefault(); this._showNext() }
    else if (e.key === "ArrowUp") { e.preventDefault(); this.add() }
    else if (e.key === "Escape") {
      const link = this.element.querySelector("a[href]")
      if (link) window.location = link.href
    }
  }

  animateCard(card, direction) {
    // Reset flip state before animating out
    const photo = card.querySelector("[data-photo-view]")
    const recipe = card.querySelector("[data-recipe-view]")
    const actions = card.querySelector("[data-photo-actions]")
    if (photo) photo.classList.remove("hidden")
    if (recipe) recipe.classList.add("hidden")
    if (actions) actions.classList.remove("hidden")

    const x = direction === "right" ? 400 : -400
    card.style.transform = `translateX(${x}px) rotate(${x * 0.05}deg)`
    card.style.opacity = "0"
    card.style.transition = "transform 0.3s, opacity 0.3s"

    setTimeout(() => {
      card.classList.add("hidden")
      card.style.transform = ""
      card.style.opacity = ""
      card.style.transition = ""

      this.currentIndex++

      if (this.newAdds >= this.openSlotsValue) {
        this.showDone()
      } else if (this.currentIndex >= this.cardTargets.length) {
        // Loop back to start — reshuffle the skipped cards
        this.currentIndex = 0
        this.cardTargets.forEach((c) => {
          c.classList.add("hidden")
          c.style.transform = ""
          c.style.opacity = ""
        })
        const first = this.cardTargets[0]
        first.classList.remove("hidden")
      } else {
        const next = this.cardTargets[this.currentIndex]
        next.classList.remove("hidden")
        next.style.position = ""
        next.style.inset = ""
      }
    }, 300)
  }

  async saveEntry(recipeId, date) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    let body = `recipe_id=${recipeId}`
    if (date) body += `&planned_date=${date}`
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": token,
        // Accept the turbo-stream response so the server can live-update
        // the picker row with the saved recipe.
        Accept: "text/vnd.turbo-stream.html, text/html",
      },
      body: body,
    })
    if (response.ok) {
      const text = await response.text()
      if (text && response.headers.get("content-type")?.includes("turbo-stream")) {
        Turbo.renderStreamMessage(text)
      }
    }
  }

  updateCounter() {
    const total = this.initialCount + this.newAdds
    this.counterTarget.textContent = `${total} of 7`

    this.progressDotTargets.forEach((dot, i) => {
      dot.className = `h-1 flex-1 rounded-full ${i < total ? "bg-warm-500" : "bg-white/20"}`
    })
  }

  showDone() {
    if (this.hasStackTarget) this.stackTarget.classList.add("hidden")
    if (this.hasActionsTarget) this.actionsTarget.classList.add("hidden")
    this.doneTarget.classList.remove("hidden")
    this.doneCountTarget.textContent = this.initialCount + this.newAdds
  }
}
